import React, { useState, useMemo, useCallback } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { ChevronDown, ChevronRight, Settings, Share2 } from 'lucide-react';
import { useComponentMetrics, getMetricLabel } from '../utils/metricsUtils';
import MetricsConfigModal from './MetricsConfigModal';
import ModuleVisibilityToggle from './ModuleVisibilityToggle';

import { calculateVariation, makeRenderVariation } from '../utils/variationUtils';
/**
 * MatomoChannelsChart - Composant pour afficher les canaux d'acquisition Matomo
 * Design identique au composant Appareils de Google Ads avec lignes dépliables
 */


/*
 * ─── Les colonnes, décrites une fois ───────────────────────────────────────────────────────
 *
 * **Le tableau rendait ses colonnes dans un ordre figé**, recopié à chaque niveau de la
 * hiérarchie. Choisir le modèle « E-commerce » réglait bien *quelles* métriques afficher, mais
 * pas leur ordre : la configuration portait une suite, l'écran en imposait une autre.
 *
 * ⚠️ **La ligne courante et la ligne précédente ne portent pas les mêmes noms.** La première est
 * normalisée par le composant (`visits`, `uniqueVisitors`, `pageviews`), la seconde arrive brute
 * de Matomo (`nb_visits`, `nb_uniq_visitors`, `nb_actions`), avec un taux de rebond en chaîne
 * — « 12.3% ». D'où deux accesseurs par colonne : confondre les deux ferait une variation
 * calculée contre `undefined`, c'est-à-dire −100 % partout.
 */
const COLONNES = {
  visites: {
    champ: 'visits',
    valeur: (l) => l.visits || 0,
    precedent: (p) => p.nb_visits || 0,
    format: (v, f) => f(v)
  },
  visiteurs: {
    champ: 'uniqueVisitors',
    valeur: (l) => l.uniqueVisitors || 0,
    precedent: (p) => p.nb_uniq_visitors || 0,
    format: (v, f) => f(v)
  },
  rebond: {
    champ: 'bounceRate',
    valeur: (l) => l.bounceRate || 0,
    // Matomo rend « 12.3% » : une chaîne, que `calculateVariation` prendrait pour zéro.
    precedent: (p) => parseFloat(String(p.bounce_rate || '0').replace('%', '')) || 0,
    format: (v, f) => `${f(v, 1)}%`,
    inverse: true
  },
  pages_vues: {
    champ: 'pageviews',
    valeur: (l) => l.pageviews || 0,
    precedent: (p) => p.nb_actions || 0,
    format: (v, f) => f(v)
  },
  taux_conv: {
    champ: 'conversionRate',
    valeur: (l) => l.conversionRate || 0,
    precedent: (p) => (p.nb_visits > 0 ? ((p.nb_visits_converted || 0) / p.nb_visits * 100) : 0),
    format: (v, f) => `${f(v, 2)}%`
  },
  conv: {
    champ: 'conversions',
    valeur: (l) => l.conversions || 0,
    precedent: (p) => p.nb_visits_converted || 0,
    format: (v, f) => f(v)
  },
  valeur_conv: {
    champ: 'conversionsValue',
    valeur: (l) => l.conversionsValue || 0,
    precedent: (p) => p.revenue || p.goal_revenue || 0,
    format: (v, f) => `${f(v, 2)}\u20ac`
  },
  duree_moy: {
    champ: 'avgTimeOnSite',
    valeur: (l) => l.avgTimeOnSite || 0,
    precedent: (p) => p.avg_time_on_site || 0,
    duree: true
  },
  panier_moyen: {
    champ: 'avgOrderValue',
    valeur: (l) => (l.conversions > 0 ? (l.conversionsValue || 0) / l.conversions : 0),
    precedent: (p) => (p.nb_visits_converted > 0 ? (p.revenue || 0) / p.nb_visits_converted : 0),
    format: (v, f) => `${f(v, 2)}\u20ac`
  },
  pages_visite: {
    champ: 'actionsPerVisit',
    valeur: (l) => l.actionsPerVisit || 0,
    precedent: (p) => parseFloat(p.nb_actions_per_visit || 0) || 0,
    format: (v, f) => f(v, 2)
  },
  transactions: {
    champ: 'transactions',
    valeur: (l) => l.transactions || 0,
    precedent: (p) => p.transactions || 0,
    format: (v, f) => f(v, 0)
  },
  chiffre_affaires: {
    champ: 'totalRevenue',
    valeur: (l) => l.totalRevenue || 0,
    precedent: (p) => p.totalRevenue || 0,
    format: (v, f) => `${f(v, 2)}\u20ac`
  },
  taux_transaction: {
    champ: 'transactionRate',
    valeur: (l) => l.transactionRate || 0,
    precedent: (p) => p.transactionRate || 0,
    format: (v, f) => `${f(v, 2)}%`
  }
};

/** La durée, en minutes:secondes. */
const formaterDuree = (secondes) => {
  const minutes = Math.floor((secondes || 0) / 60);
  const reste = Math.floor((secondes || 0) % 60);
  return `${minutes}:${reste.toString().padStart(2, '0')}`;
};

const MatomoChannelsChart = ({
  channels = [],
  previousChannels = [],
  /**
   * Le nombre de visiteurs du site sur la période.
   *
   * **Les visiteurs uniques ne s'additionnent pas** : chaque rapport Matomo rend, par ligne, la
   * somme de ses uniques quotidiens, et un visiteur présent dans deux lignes y compte deux fois.
   * Le pied affichait donc trois totaux différents pour la même grandeur selon le composant —
   * 7 836 ici, 7 075 aux appareils, 6 909 à la géographie.
   *
   * Il affiche désormais le compte du site, celui de la carte Utilisateurs. Les lignes gardent
   * leur valeur : elles ne s'additionnent pas, et c'est vrai de tous les outils de mesure.
   */
  visiteursDuSite = null,
  compareEnabled = false,
  formatNumber,
  loading = false,
  accountId = 'global',
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'matomo_channels'
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'visits', direction: 'desc' });
  const [expandedChannels, setExpandedChannels] = useState({});
  const [expandedSources, setExpandedSources] = useState({}); // Pour le niveau 2 (sources dans campagnes)
  const [currentPage, setCurrentPage] = useState(1);
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);
  const ITEMS_PER_PAGE = 10;

  // Hook pour les métriques du composant (réactif aux changements)
  const componentMetrics = useComponentMetrics('matomo_channels', accountId);

  // Fonction helper pour vérifier si une métrique est sélectionnée
  const isMetricActive = useCallback((metricKey) => {
    return componentMetrics.metrics.includes(metricKey);
  }, [componentMetrics.metrics]);

  /**
   * L'intitulé d'une colonne, pris dans la table commune aux trois solutions de mesure.
   *
   * Les intitulés étaient écrits en dur — « Visites », « Visiteurs », « Pages/Visite ». Renommer
   * la table des libellés n'avait donc aucun effet ici, et le tableau continuait d'annoncer
   * autre chose que le reste de l'application : c'est ainsi que « Utilisateurs » restait
   * introuvable alors que la colonne était bien là.
   */
  const libelleColonne = (cle) => getMetricLabel(cle, 'matomo_channels', accountId);

  /*
   * Les colonnes à rendre, **dans l'ordre du modèle**.
   *
   * `componentMetrics.order` porte la suite choisie ; `metrics` dit lesquelles sont actives.
   * Une clé inconnue de `COLONNES` vient d'un modèle conçu pour une autre famille : on l'écarte.
   */
  const colonnes = useMemo(() => {
    const ordre = componentMetrics.order?.length ? componentMetrics.order : componentMetrics.metrics;
    return (ordre || [])
      .filter((cle) => COLONNES[cle] && isMetricActive(cle))
      .map((cle) => ({ cle, ...COLONNES[cle] }));
  }, [componentMetrics.order, componentMetrics.metrics, isMetricActive]);


  /**
   * Ce que le camembert de droite représente.
   *
   * Il montrait les conversions quel que soit le tableau — y compris sur un compte e-commerce
   * où la colonne Conversions n'est pas affichée, et où ce sont les **transactions** qu'on
   * regarde. Le graphique et le tableau annonçaient alors deux choses différentes sans que rien
   * ne le dise.
   *
   * Les transactions l'emportent quand elles sont affichées — c'est le choix d'un compte
   * marchand, et le modèle « E-commerce » les active. À défaut, les conversions. Si aucune des
   * deux n'est à l'écran, le camembert disparaît : il n'aurait rien à représenter.
   */
  const mesureDuCamembert = isMetricActive('transactions')
    ? { cle: 'transactions', champ: 'transactions' }
    : (isMetricActive('conv') ? { cle: 'conv', champ: 'conversions' } : null);


  const COLORS = ['#0088FE', '#82ca9d', '#FFBB28', '#FF8042', '#8884d8', '#00C49F', '#FF6B6B', '#4ECDC4'];


  // Fonction pour afficher une variation
  const renderVariation = makeRenderVariation({ fontSize: '11px', format: (v) => formatNumber(v, 1) });

  // Mapping des noms de canaux Matomo vers des noms français
  const channelNameMapping = {
    'Direct Entry': 'Accès direct',
    'Direct': 'Accès direct',
    'direct': 'Accès direct',
    'Search Engines': 'Moteurs de recherche',
    'search': 'Moteurs de recherche',
    'Websites': 'Sites référents',
    'websites': 'Sites référents',
    'Campaigns': 'Campagnes',
    'campaigns': 'Campagnes',
    'Social Networks': 'Réseaux sociaux',
    'social': 'Réseaux sociaux'
  };

  // Normaliser les données des canaux
  const channelData = useMemo(() => {
    if (!channels || !Array.isArray(channels) || channels.length === 0) {
      return [];
    }

    return channels.map(channel => {
      const rawName = channel.label || channel.name || channel.referrer_type || 'Autre';
      const name = channelNameMapping[rawName] || rawName;

      const visits = channel.nb_visits || 0;
      /**
       * ⚠️ `conversions` et non `nb_visits_converted`.
       *
       * Le second compte les **visites** ayant converti : une visite atteignant trois
       * objectifs y comptait pour 1, et la colonne ne disait donc pas la même chose que celle
       * des régies ni que la carte au-dessus du tableau. Le service rend désormais le
       * dénombrement de l'objectif retenu sous `conversions`.
       */
      const conversions = channel.conversions ?? (channel.nb_visits_converted || 0);
      const conversionsValue = channel.conversionsValue ?? (channel.revenue || channel.goal_revenue || 0);

      // Normaliser les détails (sous-éléments)
      // Gérer le cas spécial des campagnes avec structure imbriquée (Source > Campagnes)
      const isNested = channel.isNested || false;
      const details = (channel.details || []).map(detail => {
        const detailVisits = detail.nb_visits || 0;
        const detailConversions = detail.nb_visits_converted || 0;

        // Pour les campagnes, inclure les sous-campagnes
        const subItems = (detail.campaigns || []).map(campaign => {
          const campVisits = campaign.nb_visits || 0;
          const campConversions = campaign.nb_visits_converted || 0;
          return {
            label: campaign.label || 'Inconnu',
            visits: campVisits,
            uniqueVisitors: campaign.nb_uniq_visitors || 0,
            pageviews: campaign.nb_actions || 0,
            conversions: campConversions,
            conversionRate: campVisits > 0 ? (campConversions / campVisits * 100) : 0,
            conversionsValue: campaign.revenue || 0,
            // Les quatre mesures e-commerce, telles que le service les rend désormais —
            // mêmes noms que GA4 et Piano.
            transactions: campaign.transactions || 0,
            totalRevenue: campaign.totalRevenue || 0,
            transactionRate: campaign.transactionRate || 0,
            avgOrderValue: campaign.avgOrderValue || 0,
            bounceRate: campaign.bounce_rate ? parseFloat(String(campaign.bounce_rate).replace('%', '')) : 0
          };
        });

        return {
          label: detail.label || 'Inconnu',
          visits: detailVisits,
          uniqueVisitors: detail.nb_uniq_visitors || 0,
          pageviews: detail.nb_actions || 0,
          conversions: detailConversions,
          conversionRate: detailVisits > 0 ? (detailConversions / detailVisits * 100) : 0,
          conversionsValue: detail.revenue || 0,
          transactions: detail.transactions || 0,
          totalRevenue: detail.totalRevenue || 0,
          transactionRate: detail.transactionRate || 0,
          avgOrderValue: detail.avgOrderValue || 0,
          bounceRate: detail.bounce_rate ? parseFloat(String(detail.bounce_rate).replace('%', '')) : 0,
          subItems: subItems,
          hasSubItems: subItems.length > 0
        };
      });

      return {
        name,
        rawName, // Garder le nom original pour le matching des détails
        visits,
        uniqueVisitors: channel.nb_uniq_visitors || 0,
        pageviews: channel.nb_actions || 0,
        conversions,
        conversionRate: visits > 0 ? (conversions / visits * 100) : 0,
        conversionsValue,
        transactions: channel.transactions || 0,
        totalRevenue: channel.totalRevenue || 0,
        transactionRate: channel.transactionRate || 0,
        avgOrderValue: channel.avgOrderValue || 0,
        bounceRate: channel.bounce_rate ? parseFloat(String(channel.bounce_rate).replace('%', '')) : 0,
        avgTimeOnSite: channel.avg_time_on_site || 0,
        actionsPerVisit: channel.nb_actions_per_visit || 0,
        details,
        hasDetails: details.length > 0
      };
    }).filter(c => c.visits > 0);
  }, [channels]);

  // Calcul des totaux
  const totals = useMemo(() => {
    if (!channelData.length) return {};

    const baseTotals = channelData.reduce((acc, channel) => ({
      visits: acc.visits + (channel.visits || 0),
      uniqueVisitors: acc.uniqueVisitors + (channel.uniqueVisitors || 0),
      pageviews: acc.pageviews + (channel.pageviews || 0),
      conversions: acc.conversions + (channel.conversions || 0),
      conversionsValue: acc.conversionsValue + (channel.conversionsValue || 0),
      // Sans elles, les colonnes Transactions et Chiffre d'affaires du pied affichaient zéro.
      transactions: acc.transactions + (channel.transactions || 0),
      totalRevenue: acc.totalRevenue + (channel.totalRevenue || 0),
      /*
       * Le rebond et la durée se **pondèrent par les visites**, ils ne se somment pas.
       *
       * Le pied de tableau n'en portait aucun : il affichait un tiret, puis 0 % une fois le
       * tiret retiré. Une moyenne simple aurait été pire qu'un zéro — sur trois canaux réels,
       * elle donne 40,9 % là où la pondération donne 10,9 %, un canal à douze visites y pesant
       * autant que celui qui en apporte trois mille.
       */
      rebondPondere: acc.rebondPondere + ((channel.bounceRate || 0) * (channel.visits || 0)),
      dureePonderee: acc.dureePonderee + ((channel.avgTimeOnSite || 0) * (channel.visits || 0))
    }), {
      visits: 0, uniqueVisitors: 0, pageviews: 0, conversions: 0, conversionsValue: 0,
      transactions: 0, totalRevenue: 0, rebondPondere: 0, dureePonderee: 0
    });

    return {
      ...baseTotals,
      conversionRate: baseTotals.visits > 0 ? (baseTotals.conversions / baseTotals.visits * 100) : 0,
      bounceRate: baseTotals.visits > 0 ? baseTotals.rebondPondere / baseTotals.visits : 0,
      avgTimeOnSite: baseTotals.visits > 0 ? baseTotals.dureePonderee / baseTotals.visits : 0,
      actionsPerVisit: baseTotals.visits > 0 ? baseTotals.pageviews / baseTotals.visits : 0,
      transactionRate: baseTotals.visits > 0 ? (baseTotals.transactions / baseTotals.visits * 100) : 0,
      avgOrderValue: baseTotals.transactions > 0 ? baseTotals.totalRevenue / baseTotals.transactions : 0
    };
  }, [channelData]);

  // Calcul des totaux de la période précédente
  const previousTotals = useMemo(() => {
    if (!compareEnabled || !previousChannels || !previousChannels.length) return null;

    const baseTotals = previousChannels.reduce((acc, channel) => ({
      visits: acc.visits + (channel.nb_visits || 0),
      uniqueVisitors: acc.uniqueVisitors + (channel.nb_uniq_visitors || 0),
      pageviews: acc.pageviews + (channel.nb_actions || 0),
      conversions: acc.conversions + (channel.nb_visits_converted || 0),
      conversionsValue: acc.conversionsValue + (channel.revenue || channel.goal_revenue || 0),
      /*
       * Les lignes de la période précédente arrivent **brutes de Matomo** : le rebond y est une
       * chaîne (« 4.4% »). Sans ces trois cumuls, les variations du pied comparaient contre
       * `undefined`, c'est-à-dire −100 % sur chaque colonne concernée.
       */
      transactions: acc.transactions + (channel.transactions || 0),
      totalRevenue: acc.totalRevenue + (channel.totalRevenue || 0),
      rebondPondere: acc.rebondPondere
        + (parseFloat(String(channel.bounce_rate || '0').replace('%', '')) || 0) * (channel.nb_visits || 0)
    }), {
      visits: 0, uniqueVisitors: 0, pageviews: 0, conversions: 0, conversionsValue: 0,
      transactions: 0, totalRevenue: 0, rebondPondere: 0
    });

    return {
      ...baseTotals,
      conversionRate: baseTotals.visits > 0 ? (baseTotals.conversions / baseTotals.visits * 100) : 0,
      bounceRate: baseTotals.visits > 0 ? baseTotals.rebondPondere / baseTotals.visits : 0,
      actionsPerVisit: baseTotals.visits > 0 ? baseTotals.pageviews / baseTotals.visits : 0,
      transactionRate: baseTotals.visits > 0 ? (baseTotals.transactions / baseTotals.visits * 100) : 0,
      avgOrderValue: baseTotals.transactions > 0 ? baseTotals.totalRevenue / baseTotals.transactions : 0
    };
  }, [compareEnabled, previousChannels]);

  // Données triées
  const sortedChannels = useMemo(() => {
    if (!channelData.length) return [];

    const sorted = [...channelData];
    sorted.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key === 'name') {
        return sortConfig.direction === 'asc'
          ? String(aValue).localeCompare(String(bValue))
          : String(bValue).localeCompare(String(aValue));
      }

      aValue = Number(aValue) || 0;
      bValue = Number(bValue) || 0;

      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });
    return sorted;
  }, [channelData, sortConfig]);

  // Pagination
  const totalPages = Math.ceil(sortedChannels.length / ITEMS_PER_PAGE);
  const paginatedChannels = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return sortedChannels.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [sortedChannels, currentPage]);

  // Données pour le pie chart Visites
  const visitsChartData = useMemo(() => {
    if (!channelData.length) return [];

    return channelData
      .map(channel => ({
        name: channel.name,
        value: channel.visits || 0,
        percentage: totals.visits > 0 ? ((channel.visits / totals.visits) * 100).toFixed(1) : 0
      }))
      .filter(channel => parseFloat(channel.percentage) > 0);
  }, [channelData, totals.visits]);

  // Données pour le pie chart Conversions

  /**
   * ⚠️ **Déclaré ici, et non avec `mesureDuCamembert` plus haut.**
   *
   * Le calcul lit `channelData`, défini au-dessus : placé avant, il lèverait une `ReferenceError`
   * à chaque rendu — et React démonte alors tout l'arbre, ce qui se voit comme une page
   * blanche. Ni le build ni la règle `no-use-before-define` ne l'empêchent par défaut.
   */
  /** Le total de la mesure représentée, calculé sur les lignes affichées. */
  const totalDuCamembert = useMemo(() => (
    mesureDuCamembert
      ? (channelData || []).reduce((somme, ligne) => somme + (ligne[mesureDuCamembert.champ] || 0), 0)
      : 0
  ), [channelData]);

  const conversionsChartData = useMemo(() => {
    if (!channelData.length || !mesureDuCamembert) return [];

    return channelData
      .map(channel => ({
        name: channel.name,
        value: channel[mesureDuCamembert.champ] || 0,
        percentage: totalDuCamembert > 0 ? (((channel[mesureDuCamembert.champ] || 0) / totalDuCamembert) * 100).toFixed(1) : 0
      }))
      .filter(channel => parseFloat(channel.percentage) > 0);
  }, [channelData, mesureDuCamembert, totalDuCamembert]);

  // Données pour la légende
  const legendData = useMemo(() => {
    if (!channelData.length) return [];

    return channelData.map((channel, index) => ({
      value: channel.name,
      color: COLORS[index % COLORS.length]
    }));
  }, [channelData]);

  // Handler de tri
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Handler pour déplier/replier un canal
  const toggleChannel = (channelName) => {
    setExpandedChannels(prev => ({
      ...prev,
      [channelName]: !prev[channelName]
    }));
  };

  // Handler pour déplier/replier une source (niveau 2)
  const toggleSource = (channelName, sourceLabel) => {
    const key = `${channelName}-${sourceLabel}`;
    setExpandedSources(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Ne pas afficher si pas de données ou en chargement
  if (loading) {
    return (
      <>
        <div className="section-header">
          <h4><Share2 size={18} />&nbsp;Canaux</h4>
        </div>
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          Chargement des données...
        </div>
      </>
    );
  }

  if (!channelData.length) {
    return null;
  }

  return (
    <>
      <div className="section-header">
        <h4><Share2 size={18} />&nbsp;Canaux</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          {toggleClientModuleVisibility ? (
            <ModuleVisibilityToggle
              isVisible={isClientModuleVisible ? isClientModuleVisible(moduleId) : true}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title="Canaux Matomo"
            />
          ) : (
            <button
              onClick={() => setIsVisible(!isVisible)}
              className="section-toggle-btn"
              title={isVisible ? "Masquer la section" : "Afficher la section"}
            >
              {isVisible ? '−' : '+'}
            </button>
          )}
        </div>
      </div>

      {isVisible && (
        <>
          {/* Graphiques en secteurs côte à côte */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                  {/* Pie chart Visites (gauche) */}
                  <div style={{ flex: 1, textAlign: 'center' }}>
                    <h5 style={{ margin: '0 0 10px 0', color: '#374151', fontSize: '14px' }}>Sessions par canal</h5>
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={visitsChartData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ percentage }) => `${percentage}%`}
                          outerRadius={70}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {visitsChartData.map((_, index) => (
                            <Cell key={`visits-cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value, name) => [formatNumber(value) + ' sessions', name]} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Légende commune (centre) */}
                  <div style={{
                    minWidth: '150px',
                    padding: '0 20px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}>
                    {legendData.map((item, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '8px',
                        fontSize: '13px'
                      }}>
                        <div style={{
                          width: '12px',
                          height: '12px',
                          backgroundColor: item.color,
                          marginRight: '8px',
                          borderRadius: '2px'
                        }}></div>
                        <span>{item.value}</span>
                      </div>
                    ))}
                  </div>

                  {/* Pie chart Conversions (droite) */}
                  <div style={{ flex: 1, textAlign: 'center' }}>
                    <h5 style={{ margin: '0 0 10px 0', color: '#374151', fontSize: '14px' }}>{mesureDuCamembert ? `${libelleColonne(mesureDuCamembert.cle)} par canal` : 'Conversions par canal'}</h5>
                    {totalDuCamembert > 0 ? (
                      <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                          <Pie
                            data={conversionsChartData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ percentage }) => `${percentage}%`}
                            outerRadius={70}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {conversionsChartData.map((_, index) => (
                              <Cell key={`conversions-cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value, name) => [`${formatNumber(value)} ${libelleColonne(mesureDuCamembert.cle).toLowerCase()}`, name]} />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div style={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
                        {/* Le modèle « Contenu » n'active ni l'une ni l'autre : le dire
                            vaut mieux qu'un cadre vide et sans titre. */}
                        {mesureDuCamembert
                          ? `Aucune ${libelleColonne(mesureDuCamembert.cle).toLowerCase()}`
                          : 'Activez Conversions ou Transactions pour ce graphique'}
                      </div>
                    )}
                </div>
              </div>
            </div>

          {/* Tableau détaillé */}
          <div className="device-table-wrapper" style={{ marginTop: '0', paddingTop: '0' }}>
            <div className="device-summary-table" style={{ marginTop: '0', paddingTop: '0' }}>
              <table
                className="devices-table"
                style={{ width: '100%', tableLayout: 'auto', borderCollapse: 'collapse' }}
              >
                <thead>
                  <tr>
                    <th onClick={() => handleSort('name')} className="sortable">
                      Canal {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                    {colonnes.map((colonne) => (
                      <th
                        key={colonne.cle}
                        onClick={() => handleSort(colonne.champ)}
                        className="sortable numeric"
                      >
                        {libelleColonne(colonne.cle)} {sortConfig.key === colonne.champ && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {paginatedChannels.map((channel, index) => {
                    // Trouver le canal correspondant dans les données de la période précédente
                    const prev = compareEnabled && previousChannels ? previousChannels.find(p =>
                      p.label === channel.rawName || p.name === channel.rawName || p.referrer_type === channel.rawName
                    ) : null;

                    return (
                    <React.Fragment key={index}>
                      {/* Ligne principale du canal */}
                      <tr
                        className={channel.hasDetails ? 'expandable-row' : ''}
                        onClick={() => channel.hasDetails && toggleChannel(channel.name)}
                        style={{ cursor: channel.hasDetails ? 'pointer' : 'default' }}
                      >
                        <td className="device-name">
                          {channel.hasDetails && (
                            <span style={{ marginRight: '4px', display: 'inline-flex', alignItems: 'center' }}>
                              {expandedChannels[channel.name] ? (
                                <ChevronDown size={14} />
                              ) : (
                                <ChevronRight size={14} />
                              )}
                            </span>
                          )}
                          <span
                            className="device-indicator"
                            style={{ backgroundColor: COLORS[(index + (currentPage - 1) * ITEMS_PER_PAGE) % COLORS.length] }}
                          ></span>
                          {channel.name}
                        </td>
                        {colonnes.map((colonne) => (
                          <td key={colonne.cle} className="numeric">
                            {colonne.duree
                              ? formaterDuree(colonne.valeur(channel))
                              : colonne.format(colonne.valeur(channel), formatNumber)}
                            {prev && renderVariation(
                              calculateVariation(colonne.valeur(channel), colonne.precedent(prev)),
                              colonne.inverse
                            )}
                          </td>
                        ))}
                      </tr>

                      {/* Sous-lignes de détails niveau 2 (si dépliées) */}
                      {expandedChannels[channel.name] && channel.details.map((detail, detailIndex) => {
                        const sourceKey = `${channel.name}-${detail.label}`;
                        const isSourceExpanded = expandedSources[sourceKey];

                        return (
                          <React.Fragment key={`${index}-detail-${detailIndex}`}>
                            {/* Ligne de détail niveau 2 */}
                            <tr
                              className={detail.hasSubItems ? 'detail-row expandable-row' : 'detail-row'}
                              onClick={(e) => {
                                if (detail.hasSubItems) {
                                  e.stopPropagation();
                                  toggleSource(channel.name, detail.label);
                                }
                              }}
                              style={{
                                backgroundColor: '#f9fafb',
                                fontSize: '0.9em',
                                cursor: detail.hasSubItems ? 'pointer' : 'default'
                              }}
                            >
                              <td className="device-name" style={{ paddingLeft: '36px' }}>
                                {detail.hasSubItems && (
                                  <span style={{ marginRight: '4px', display: 'inline-flex', alignItems: 'center' }}>
                                    {isSourceExpanded ? (
                                      <ChevronDown size={12} />
                                    ) : (
                                      <ChevronRight size={12} />
                                    )}
                                  </span>
                                )}
                                <span style={{ color: '#6b7280' }}>{detail.hasSubItems ? '' : '└'}</span> {detail.label}
                              </td>
                              {colonnes.map((colonne) => (
                                <td key={colonne.cle} className="numeric" style={{ color: '#6b7280' }}>
                                  {colonne.duree
                                    ? formaterDuree(colonne.valeur(detail))
                                    : colonne.format(colonne.valeur(detail), formatNumber)}
                                </td>
                              ))}
                            </tr>

                            {/* Sous-lignes niveau 3 (campagnes dans sources) */}
                            {isSourceExpanded && detail.subItems && detail.subItems.map((subItem, subIndex) => (
                              <tr
                                key={`${index}-detail-${detailIndex}-sub-${subIndex}`}
                                className="sub-detail-row"
                                style={{
                                  backgroundColor: '#f3f4f6',
                                  fontSize: '0.85em'
                                }}
                              >
                                <td className="device-name" style={{ paddingLeft: '56px' }}>
                                  <span style={{ color: '#9ca3af' }}>└</span> {subItem.label}
                                </td>
                                {colonnes.map((colonne) => (
                                  <td key={colonne.cle} className="numeric" style={{ color: '#9ca3af' }}>
                                    {colonne.duree
                                      ? formaterDuree(colonne.valeur(subItem))
                                      : colonne.format(colonne.valeur(subItem), formatNumber)}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </React.Fragment>
                        );
                      })}
                    </React.Fragment>
                  );})}
                </tbody>

                <tfoot>
                  <tr className="total-row">
                    <td className="device-name"><strong>TOTAL</strong></td>
                    {colonnes.map((colonne) => (
                      <td key={colonne.cle} className="numeric">
                        <strong>{colonne.cle === 'visiteurs' && visiteursDuSite !== null
                          ? formatNumber(visiteursDuSite)
                          : colonne.duree
                            ? formaterDuree(colonne.valeur(totals))
                            : colonne.format(colonne.valeur(totals), formatNumber)}</strong>
                        {!(colonne.cle === 'visiteurs' && visiteursDuSite !== null)
                          && previousTotals && renderVariation(
                            calculateVariation(colonne.valeur(totals), colonne.valeur(previousTotals)),
                            colonne.inverse
                          )}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination-controls">
                  <div className="pagination-info">
                    Affichage {((currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, sortedChannels.length)} sur {sortedChannels.length} canaux
                  </div>
                  <div className="pagination-buttons">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="pagination-btn"
                    >
                      Précédent
                    </button>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="pagination-btn"
                    >
                      Suivant
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Modal de configuration des métriques */}
      <MetricsConfigModal
        isOpen={showMetricsConfig}
        onClose={() => setShowMetricsConfig(false)}
        componentId="matomo_channels"
        accountId={accountId}
        componentTitle="Canaux"
      />
    </>
  );
};

export default MatomoChannelsChart;
